// src/app/routes.tsx
import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';

import Layout        from '@/shared/components/Layout/Layout';
import Login         from '@/features/system/auth/Login';
import RequireAuth   from '@/features/system/auth/RequireAuth';

import UsageDashboard         from '@/features/usage/components/UsageDashboard';
import StockDashboard         from '@/features/services/stock/components/StockDashboard';
import DeveloperDashboard     from '@/features/system/developer/components/DeveloperDashboard';
import SalesPurchaseDashboard from '@/features/services/sales/components/SalesPurchaseDashboard';
import AdminDashboard         from '@/features/system/admin/components/AdminDashboard';
import Dashboard_media        from '@/features/services/media/components/Dashboard_media';
import Dashboard_media_youtube from '@/features/services/media/components/Dashboard_media_youtube';

import RoleSwitch from '@/shared/auth/RoleSwitch';
import Require    from '@/shared/auth/Require';
import { Role }   from '@/shared/auth/roles';

// Feature route modules
import { ttlockRoutes } from '@/features/services/ttlock/routes';

// Lazy chunks
const TableOrder = lazy(() =>
  import('@/features/services/order/components/TableOrder')
);
const OrderDashboard = lazy(() =>
  import('@/features/services/order/components/OrderDashboard')
);

// All routes that require auth & the app layout
const protectedChildren: RouteObject[] = [
  { index: true, element: <UsageDashboard /> },

  {
    path: 'cash-register',
    element: (
      <Suspense fallback={<div className="p-4">Loading…</div>}>
        <DeveloperDashboard />
      </Suspense>
    ),
  },

  {
    path: 'order',
    element: (
      <Suspense fallback={<div className="p-4">Loading order…</div>}>
        <RoleSwitch
          roles={[Role.SuperAdmin, Role.PurchaseTableAdmin]}
          whenAllowed={TableOrder}
          whenDenied={OrderDashboard}
        />
      </Suspense>
    ),
  },

  { path: 'stock',          element: <StockDashboard /> },
  { path: 'sales-purchase', element: <SalesPurchaseDashboard /> },
  { path: 'media',              element: <Dashboard_media /> },
  { path: 'media/youtube',      element: <Dashboard_media_youtube /> },
  { path: 'developer',      element: <DeveloperDashboard /> },
  { path: 'admin',          element: <AdminDashboard /> },

  // Plug in TTLock feature routes (absolute or relative are both fine)
  ...ttlockRoutes,

  // NotFound
  { path: '*', element: <div className="p-6">Not found</div> },
];

export const router = createBrowserRouter([
  // Public
  { path: '/login', element: <Login /> },

  // Guarded area
  {
    element: <RequireAuth />, // <- uses <Outlet/> internally
    children: [
      {
        element: <Layout />, // app shell
        children: protectedChildren,
      },
    ],
  },
]);
